import { formatPeakWindow, type PeakWindow } from "@logic/lib/battery-arbitrage"

import {
  Disclosure,
  Eyebrow,
  GradText,
  Icon,
  Section,
  SectionHeading,
  TINT_BG,
  TINT_TEXT,
  TINT_VAR,
} from "../primitives"
import {
  BATTERY_POINTS,
  CRISIS_CARDS,
  GENERATOR_POINTS,
  OUTAGE_TIMELINE,
} from "../static-copy"
import { useDesktopViewport } from "../use-desktop-viewport"

export function Crisis(): JSX.Element {
  const isDesktop = useDesktopViewport()
  return (
    <Section id="why-now" className="pb-3.5">
      <SectionHeading
        eyebrow="Why now"
        sub="Three pressures are converging on the same wire into your house."
      >
        The energy crisis <GradText>hits home</GradText>.
      </SectionHeading>
      {isDesktop ? (
        <div className="grid gap-8 lg:grid-cols-3">
          {CRISIS_CARDS.map((card, index) => (
            <div key={card.kicker} className="relative text-center">
              <span
                aria-hidden="true"
                className="font-display pointer-events-none absolute top-8 left-1/2 -translate-x-1/2 text-[9rem] leading-none font-semibold tabular-nums opacity-[0.06]"
                style={{ color: TINT_VAR[card.tint] }}
              >
                {index + 1}
              </span>
              <div className="relative">
                <div
                  className={`mx-auto mb-5 grid size-11 place-items-center rounded-full border border-bp-border-strong ${TINT_TEXT[card.tint]}`}
                >
                  <Icon name={card.icon} className="size-5" />
                </div>
                <div
                  className={`font-display text-[length:var(--step-2)] leading-[1.05] font-semibold tracking-[-0.025em] ${TINT_TEXT[card.tint]}`}
                >
                  {card.head}
                </div>
                <Eyebrow className="mt-1.5">{card.kicker}</Eyebrow>
                <span
                  aria-hidden="true"
                  className={`mx-auto mt-5 block h-px w-10 ${TINT_BG[card.tint]}`}
                />
                <p className="mx-auto mt-5 max-w-[340px] text-[length:var(--step--1)] leading-relaxed text-bp-text-muted">
                  {card.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border-t border-bp-border">
          {CRISIS_CARDS.map((card) => (
            <div
              key={card.kicker}
              className="border-b border-bp-border py-6 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-full border border-bp-border-strong ${TINT_TEXT[card.tint]}`}
                >
                  <Icon name={card.icon} className="size-4" />
                </span>
                <span>
                  <span
                    className={`font-display block text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.02em] ${TINT_TEXT[card.tint]}`}
                  >
                    {card.head}
                  </span>
                  <Eyebrow className="mt-0.5 text-[10px]">{card.kicker}</Eyebrow>
                </span>
              </div>
              <p className="mt-3.5 text-[length:var(--step--1)] leading-relaxed text-bp-text-muted">
                {card.body}
              </p>
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

const HOURS_IN_DAY = 24

const isPeakHour = (hour: number, window: PeakWindow): boolean =>
  hour >= window.startHour && hour < window.endHour

/** Left edge and width of a window as percentages of a 24-hour strip. */
const peakWindowBounds = (
  window: PeakWindow,
): { leftPct: number; widthPct: number } => ({
  leftPct: (window.startHour / HOURS_IN_DAY) * 100,
  widthPct: ((window.endHour - window.startHour) / HOURS_IN_DAY) * 100,
})

/** The window drawn before the arbitrage run has produced this home's real
 * one. Late afternoon into the evening is the shape of a residential TOU
 * tariff; the ruler labels it as typical and never names the hours, so it
 * illustrates the problem without claiming to be this home's tariff. */
const ILLUSTRATIVE_PEAK_WINDOW: PeakWindow = { startHour: 16, endHour: 21 }

/** Axis ticks every six hours — enough to read the window off the strip
 * without crowding it. */
const RULER_TICK_HOURS = [0, 6, 12, 18, 24] as const

const rulerTickLabel = (hour: number): string => {
  if (hour === 0 || hour === HOURS_IN_DAY) {
    return `12a`
  }
  if (hour === 12) {
    return `12p`
  }
  return hour < 12 ? `${hour}a` : `${hour - 12}p`
}

/** A day of hour pips with the peak window raised and tinted. */
function PeakDayRuler({
  window,
  isIllustrative,
}: {
  window: PeakWindow
  isIllustrative: boolean
}): JSX.Element {
  const { leftPct, widthPct } = peakWindowBounds(window)
  return (
    <div className="mx-auto mt-10 max-w-[560px]">
      <div className="relative mb-2 h-4">
        <span
          className="absolute text-center text-[length:var(--step--2)] font-semibold tracking-[0.14em] whitespace-nowrap text-bp-danger uppercase"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        >
          {formatPeakWindow(window)}
          {isIllustrative ? `*` : ``}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="flex items-end justify-between gap-[2px]"
        data-testid={
          isIllustrative ? `peak-day-ruler-illustrative` : `peak-day-ruler`
        }
      >
        {Array.from({ length: HOURS_IN_DAY }).map((_, hour) => {
          const peak = isPeakHour(hour, window)
          return (
            <span
              key={hour}
              className={`flex-1 rounded-full ${peak ? `bg-bp-danger` : `bg-bp-border-strong`} ${isIllustrative ? `opacity-70` : ``}`}
              style={{ height: peak ? `36px` : `12px` }}
            />
          )
        })}
      </div>
      <div aria-hidden="true" className="relative mt-2.5 h-4">
        {RULER_TICK_HOURS.map((hour) => (
          <span
            key={hour}
            className="absolute text-[length:var(--step--2)] tabular-nums text-bp-text-dim"
            style={
              hour === 0
                ? { left: `0%` }
                : hour === HOURS_IN_DAY
                  ? { right: `0%` }
                  : {
                      left: `${(hour / HOURS_IN_DAY) * 100}%`,
                      transform: `translateX(-50%)`,
                    }
            }
          >
            {rulerTickLabel(hour)}
          </span>
        ))}
      </div>
    </div>
  )
}

export function PeakTrap({
  peakWindow,
}: {
  peakWindow: PeakWindow | null
}): JSX.Element {
  const isIllustrative = peakWindow == null
  return (
    <Section>
      <div className="relative overflow-hidden rounded-lg bg-bp-glass px-6 py-16 text-center lg:px-16">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 size-[520px] -translate-x-1/2 rounded-full bg-[image:radial-gradient(circle,var(--bp-danger),transparent_65%)] opacity-[0.16]"
        />
        <div className="relative mx-auto max-w-[620px]">
          <Eyebrow className="mb-5 text-bp-danger">The peak-rate trap</Eyebrow>
          {peakWindow ? (
            <>
              <div className="font-display text-[length:var(--step-5)] leading-[0.95] font-semibold tracking-[-0.04em] tabular-nums text-bp-text">
                {formatPeakWindow(peakWindow)}
              </div>
              <div className="mt-2 text-[length:var(--step--2)] font-semibold tracking-[0.14em] text-bp-text-dim uppercase">
                Peak window in this estimate
              </div>
            </>
          ) : null}
          <h3
            className={`font-display text-[length:var(--step-1)] font-semibold tracking-[-0.02em] text-bp-text-soft ${peakWindow ? `mt-6` : ``}`}
          >
            Utilities profit from your schedule.
          </h3>
          <p className="mt-4 text-[length:var(--step-0)] leading-relaxed text-bp-text-muted">
            To shift usage patterns, utilities charge higher per-kWh rates during
            peak community demand
            {peakWindow ? `` : ` — typically late afternoon into the evening`}.
            That forces a choice: pay premium prices, or sacrifice your evening
            routine to dodge them.
          </p>
          <PeakDayRuler
            window={peakWindow ?? ILLUSTRATIVE_PEAK_WINDOW}
            isIllustrative={isIllustrative}
          />
        </div>
        {isIllustrative ? (
          <p className="relative mt-12 text-right text-[length:var(--step--3)] text-bp-text-dim">
            * A typical evening peak. A saved estimate supplies the modeled rate
            window and its assumptions.
          </p>
        ) : null}
      </div>
    </Section>
  )
}

export function HiddenCost(): JSX.Element {
  return (
    <Section id="hidden-cost">
      <SectionHeading
        eyebrow="The hidden cost"
        sub="The average American home lost power for 11 hours last year. Every hour past the first few gets more expensive."
      >
        An outage runs on a <GradText>clock</GradText>.
      </SectionHeading>
      <ol className="relative grid gap-0 lg:grid-cols-4">
        <div
          aria-hidden="true"
          className="absolute top-[11px] right-0 left-0 hidden h-px bg-gradient-to-r from-bp-border-strong via-bp-gold/60 to-bp-danger lg:block"
        />
        {OUTAGE_TIMELINE.map((moment) => (
          <li
            key={moment.at}
            className="relative border-l border-bp-border py-2 pr-6 pl-6 lg:border-l-0 lg:pl-0"
          >
            <span
              aria-hidden="true"
              className={`absolute top-[6px] -left-[6px] size-3 rounded-full border-2 border-bp-page bg-current lg:left-0 ${TINT_TEXT[moment.tint]}`}
            />
            <div className="lg:pt-8">
              <div
                className={`font-display text-[length:var(--step--1)] font-semibold tracking-[0.12em] uppercase ${TINT_TEXT[moment.tint]}`}
              >
                {moment.at}
              </div>
              <div className="font-display mt-2 text-[length:var(--step-1)] leading-tight font-semibold text-bp-text">
                {moment.head}
              </div>
              <p className="mt-3 max-w-[280px] text-[length:var(--step--1)] leading-relaxed text-bp-text-muted">
                {moment.body}
              </p>
              {moment.cost ? (
                <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-bp-border-strong bg-bp-elevated px-3 py-1.5 text-[length:var(--step--2)] font-semibold tabular-nums text-bp-text-soft">
                  <Icon
                    name="clock"
                    className={`size-3.5 ${TINT_TEXT[moment.tint]}`}
                  />
                  {moment.cost}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </Section>
  )
}

function BatteryPointRows(): JSX.Element {
  return (
    <>
      {BATTERY_POINTS.map(([label, note]) => (
        <div
          key={label}
          className="flex items-center justify-between gap-4 border-b border-bp-border py-3 last:border-0"
        >
          <div className="flex items-center gap-2.5">
            <div className="grid size-5 place-items-center rounded-full bg-bp-success-soft text-bp-success">
              <Icon name="check" className="size-3" />
            </div>
            <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
              {label}
            </span>
          </div>
          <span className="text-right text-[length:var(--step--2)] text-bp-success">
            {note}
          </span>
        </div>
      ))}
    </>
  )
}

function GeneratorPointRows(): JSX.Element {
  return (
    <>
      {GENERATOR_POINTS.map(([label, note]) => (
        <div
          key={label}
          className="flex items-center justify-between gap-4 border-b border-bp-border py-3 last:border-0"
        >
          <div className="flex items-center gap-2.5">
            <div className="grid size-5 place-items-center rounded-full bg-bp-glass-hi text-[length:var(--step--2)] font-bold text-bp-text-dim">
              ×
            </div>
            <span className="text-[length:var(--step--1)] text-bp-text-muted">
              {label}
            </span>
          </div>
          <span className="text-right text-[length:var(--step--2)] text-bp-text-dim">
            {note}
          </span>
        </div>
      ))}
    </>
  )
}

function BatteryCardHeader(): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-display text-[length:var(--step-1)] font-semibold tracking-[-0.015em] text-bp-text">
          Home battery
        </div>
        <div className="mt-1 text-[length:var(--step--2)] font-semibold text-bp-accent">
          One-time investment
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-bp-success/30 bg-bp-success-soft px-2.5 py-1 text-[length:var(--step--2)] whitespace-nowrap text-bp-success">
        <span className="size-1.5 rounded-full bg-current" /> Recommended
      </span>
    </div>
  )
}

export function SmartResponse(): JSX.Element {
  const isDesktop = useDesktopViewport()
  return (
    <Section id="response">
      <SectionHeading
        eyebrow="The smart response"
        sub="Battery storage isn't just backup power — it's the only solution that answers the crisis and can pay for itself over time."
      >
        A smarter answer than a <GradText>generator</GradText>.
      </SectionHeading>
      {isDesktop ? (
        <div className="grid gap-3.5 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-lg border border-bp-accent bg-bp-surface px-8 py-8">
            <div className="relative">
              <div className="mb-5">
                <BatteryCardHeader />
              </div>
              <BatteryPointRows />
            </div>
          </div>
          <div className="rounded-lg border border-bp-border bg-bp-elevated px-8 py-8">
            <div className="mb-5">
              <div className="font-display text-[length:var(--step-1)] font-semibold tracking-[-0.015em] text-bp-text-muted">
                Traditional generators
              </div>
              <div className="mt-1 text-[length:var(--step--2)] text-bp-text-dim">
                Never-ending costs
              </div>
            </div>
            <GeneratorPointRows />
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-bp-accent bg-bp-surface px-5 py-5">
            <div className="mb-2">
              <BatteryCardHeader />
            </div>
            <BatteryPointRows />
          </div>
          <div className="mt-2">
            <Disclosure
              summary={
                <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text-muted">
                  Compare: traditional generators
                </span>
              }
            >
              <div className="mb-2 text-[length:var(--step--2)] text-bp-text-dim">
                Never-ending costs
              </div>
              <GeneratorPointRows />
            </Disclosure>
          </div>
        </>
      )}
    </Section>
  )
}
